import dayjs from "dayjs";
import { FormikValues } from "formik";
import * as Yup from "yup";
import { RoleBlockPayload } from "store/apis/directorsAndOfficers";
import { Person, PersonRole } from "types/Models/apAutofills";
import { Shareholder } from "types/Models/Shareholders";
import {
  AP_STATUS_ACTIVE,
  AP_STATUS_FORMER,
  PERSONA_TYPE_DIRECTOR,
  PERSONA_TYPE_OFFICER,
  PERSONA_TYPE_SHAREHOLDER,
  SHAREHOLDER_TYPE_PERSONA,
} from "constants/personaRole";

export const STEP_SELECT_TYPE = "SELECT_TYPE" as const;
export const STEP_ADD_PERSON = "ADD_PERSON" as const;

export type Step = typeof STEP_SELECT_TYPE | typeof STEP_ADD_PERSON;

export const DIRECTOR_PILL = "DIRECTOR";
export const SHAREHOLDER_PILL = "SHAREHOLDER";
export const OTHER_PILL = "OTHER";

export type RoleDetail = {
  startDate: string | Date | "";
  endDate: string | Date | "";
  currentlyHolds: boolean;
  joinId?: string;
};

export type OtherRoleEntry = RoleDetail & {
  roleName: string;
  clientId: string;
};

export type PersonaTypeOption = { uuid: string; name: string };

export type APUpdateOp = {
  kind: "AP_UPDATE";
  personaType: "OFFICER" | "DIRECTOR";
  joinId: string;
  payload: {
    ap_status: "ACTIVE" | "FORMER";
    persona_autofill_id: string;
    role_id: string | null;
    designation: string | null;
    appointed_on: string | null;
    resigned_on: string | null;
    is_authorised_signatory: boolean;
  };
};

export type ShareholderUpdateOp = {
  kind: "SHAREHOLDER_UPDATE";
  shareholderId: string;
  payload: {
    instance_id: string;
    ownership_stake: string;
    shareholder_type: "PERSONA";
    country_id: string;
    issued_common_stock: number | null;
    issued_preferred_stock: number | null;
  };
};

export type RoleUpdateOp = APUpdateOp | ShareholderUpdateOp;

export const emptyRoleDetail = (): RoleDetail => ({
  startDate: "",
  endDate: "",
  currentlyHolds: true,
});

export const emptyOtherRole = (): OtherRoleEntry => ({
  roleName: "",
  clientId: crypto.randomUUID(),
  ...emptyRoleDetail(),
});

export const toApiDate = (value: string | Date | "") =>
  value ? dayjs(value).format("YYYY-MM-DD") : null;

export const isEndDateBeforeStartDate = (detail: RoleDetail) =>
  !detail.currentlyHolds &&
  !!detail.startDate &&
  !!detail.endDate &&
  dayjs(detail.endDate).isBefore(dayjs(detail.startDate), "day");

export const isMissingStartDate = (detail: RoleDetail) => !detail.startDate;

export const hasStartDate = (detail: RoleDetail) => Boolean(detail.startDate);

export const buildEditRoleDetail = (role: PersonRole): RoleDetail => ({
  startDate: role.appointed_on ? new Date(role.appointed_on) : "",
  endDate: role.resigned_on ? new Date(role.resigned_on) : "",
  currentlyHolds: role.ap_status === AP_STATUS_ACTIVE,
  joinId: role.join_id,
});

const addNamedOfficerRole = (
  role: PersonRole,
  selectedPills: Set<string>,
  roleDetails: Record<string, RoleDetail>
) => {
  const roleUuid = role.persona_role?.uuid;
  if (!roleUuid) return;
  selectedPills.add(roleUuid);
  roleDetails[roleUuid] = buildEditRoleDetail(role);
};

const addCustomOfficerRole = (
  role: PersonRole,
  selectedPills: Set<string>,
  otherRoles: OtherRoleEntry[]
) => {
  selectedPills.add(OTHER_PILL);
  otherRoles.push({
    ...buildEditRoleDetail(role),
    roleName: role.designation || "",
    clientId: crypto.randomUUID(),
  });
};

export const buildEditInitialValues = (
  person: Person,
  shareholder?: Shareholder | null
) => {
  const selectedPills = new Set<string>();
  const roleDetails: Record<string, RoleDetail> = {};
  const otherRoles: OtherRoleEntry[] = [];

  person.roles.forEach((role) => {
    switch (role.persona_type) {
      case PERSONA_TYPE_DIRECTOR:
        selectedPills.add(DIRECTOR_PILL);
        roleDetails[DIRECTOR_PILL] = buildEditRoleDetail(role);
        return;
      case PERSONA_TYPE_OFFICER:
        if (role.persona_role?.uuid) {
          addNamedOfficerRole(role, selectedPills, roleDetails);
        } else {
          addCustomOfficerRole(role, selectedPills, otherRoles);
        }
        return;
      case PERSONA_TYPE_SHAREHOLDER:
        selectedPills.add(SHAREHOLDER_PILL);
        return;
      default:
        return;
    }
  });

  return {
    personaAutofillId: person.persona_autofill_id,
    isAuthorisedSignatory: person.is_authorised_signatory,
    selectedPills: Array.from(selectedPills),
    roleDetails,
    shareholder: {
      issuedCommonStock:
        shareholder?.issued_common_stock != null
          ? String(shareholder.issued_common_stock)
          : "",
      issuedPreferredStock:
        shareholder?.issued_preferred_stock != null
          ? String(shareholder.issued_preferred_stock)
          : "",
      ownershipPercentage: shareholder?.ownership_stake ?? "",
      countryId: shareholder?.country?.uuid ?? "",
    },
    otherRoles: otherRoles.length ? otherRoles : [emptyOtherRole()],
  };
};

export const blankPersonFormValues = {
  personaAutofillId: "",
  isAuthorisedSignatory: false,
  selectedPills: [] as string[],
  roleDetails: {} as Record<string, RoleDetail>,
  shareholder: {
    issuedCommonStock: "",
    issuedPreferredStock: "",
    ownershipPercentage: "",
    countryId: "",
  },
  otherRoles: [emptyOtherRole()] as OtherRoleEntry[],
};

export const collectRoleDetailsToValidate = (
  values: FormikValues,
  personaTypes: PersonaTypeOption[]
): RoleDetail[] => {
  const details: RoleDetail[] = [];

  personaTypes.forEach((type) => {
    if (!values.selectedPills.includes(type.uuid)) return;
    details.push(
      (values.roleDetails[type.uuid] || emptyRoleDetail()) as RoleDetail
    );
  });

  if (values.selectedPills.includes(DIRECTOR_PILL)) {
    details.push(
      (values.roleDetails[DIRECTOR_PILL] || emptyRoleDetail()) as RoleDetail
    );
  }

  if (values.selectedPills.includes(OTHER_PILL)) {
    details.push(...(values.otherRoles as OtherRoleEntry[]));
  }

  return details;
};

export const buildAddPersonValidationSchema = (
  personaTypes: PersonaTypeOption[]
) =>
  Yup.object({
    personaAutofillId: Yup.string().required("Select a person"),
    selectedPills: Yup.array().min(1, "Select at least one role"),
  }).test("role-requirements", "role-requirements", function (values) {
    const formValues = values as FormikValues;

    const hasShareholderRequirements =
      !formValues.selectedPills?.includes(SHAREHOLDER_PILL) ||
      Boolean(
        formValues.shareholder?.ownershipPercentage &&
          formValues.shareholder?.countryId
      );
    if (!hasShareholderRequirements) {
      return this.createError({
        path: "shareholder.ownershipPercentage",
        message: "Ownership percentage and country are required",
      });
    }

    const hasOtherRequirements =
      !formValues.selectedPills?.includes(OTHER_PILL) ||
      (formValues.otherRoles as OtherRoleEntry[])?.some(
        (entry) => entry.roleName
      );
    if (!hasOtherRequirements) {
      return this.createError({
        path: "otherRoles",
        message: "Enter a role name for at least one other role",
      });
    }

    const rolesToValidate = collectRoleDetailsToValidate(
      formValues,
      personaTypes
    );

    if (rolesToValidate.some(isEndDateBeforeStartDate)) {
      return this.createError({
        path: "roleDetails",
        message: "End date cannot be before the start date",
      });
    }

    if (!rolesToValidate.every(hasStartDate)) {
      return this.createError({
        path: "roleDetails",
        message: "Start date is required for every role",
      });
    }

    return true;
  });

export const buildRolesForCreate = (
  values: FormikValues,
  personaTypes: PersonaTypeOption[]
): RoleBlockPayload[] => {
  const roles: RoleBlockPayload[] = [];
  let signatoryAssigned = false;

  personaTypes.forEach((type) => {
    if (!values.selectedPills.includes(type.uuid)) return;
    const detail: RoleDetail =
      values.roleDetails[type.uuid] || emptyRoleDetail();
    roles.push({
      role_type: PERSONA_TYPE_OFFICER,
      role_id: type.uuid,
      appointed_on: toApiDate(detail.startDate),
      resigned_on: toApiDate(detail.endDate),
      ap_status: detail.currentlyHolds ? AP_STATUS_ACTIVE : AP_STATUS_FORMER,
      is_authorised_signatory:
        values.isAuthorisedSignatory && !signatoryAssigned,
    });
    signatoryAssigned = signatoryAssigned || values.isAuthorisedSignatory;
  });

  if (values.selectedPills.includes(DIRECTOR_PILL)) {
    const detail: RoleDetail =
      values.roleDetails[DIRECTOR_PILL] || emptyRoleDetail();
    roles.push({
      role_type: PERSONA_TYPE_DIRECTOR,
      appointed_on: toApiDate(detail.startDate),
      resigned_on: toApiDate(detail.endDate),
      ap_status: detail.currentlyHolds ? AP_STATUS_ACTIVE : AP_STATUS_FORMER,
      is_authorised_signatory:
        values.isAuthorisedSignatory && !signatoryAssigned,
    });
    signatoryAssigned = signatoryAssigned || values.isAuthorisedSignatory;
  }

  if (values.selectedPills.includes(OTHER_PILL)) {
    (values.otherRoles as OtherRoleEntry[])
      .filter((entry) => entry.roleName)
      .forEach((entry) => {
        roles.push({
          role_type: PERSONA_TYPE_OFFICER,
          role_id: null,
          designation: entry.roleName,
          appointed_on: toApiDate(entry.startDate),
          resigned_on: toApiDate(entry.endDate),
          ap_status: entry.currentlyHolds ? AP_STATUS_ACTIVE : AP_STATUS_FORMER,
          is_authorised_signatory:
            values.isAuthorisedSignatory && !signatoryAssigned,
        });
        signatoryAssigned = signatoryAssigned || values.isAuthorisedSignatory;
      });
  }

  if (values.selectedPills.includes(SHAREHOLDER_PILL)) {
    roles.push({
      role_type: PERSONA_TYPE_SHAREHOLDER,
      ownership_stake: String(values.shareholder.ownershipPercentage),
      country_id: values.shareholder.countryId,
      issued_common_stock:
        values.shareholder.issuedCommonStock !== ""
          ? Number(values.shareholder.issuedCommonStock)
          : null,
      issued_preferred_stock:
        values.shareholder.issuedPreferredStock !== ""
          ? Number(values.shareholder.issuedPreferredStock)
          : null,
    });
  }

  return roles;
};

export const partitionRoleOperationsForEdit = (
  values: FormikValues,
  personaTypes: PersonaTypeOption[],
  activeShareholder: Shareholder | null
): { updates: RoleUpdateOp[]; newRoles: RoleBlockPayload[] } => {
  let signatoryAssigned = false;
  const updates: RoleUpdateOp[] = [];
  const newRoles: RoleBlockPayload[] = [];

  personaTypes.forEach((type) => {
    if (!values.selectedPills.includes(type.uuid)) return;
    const detail: RoleDetail =
      values.roleDetails[type.uuid] || emptyRoleDetail();
    const isSignatory = values.isAuthorisedSignatory && !signatoryAssigned;
    if (detail.joinId) {
      updates.push({
        kind: "AP_UPDATE",
        personaType: PERSONA_TYPE_OFFICER,
        joinId: detail.joinId,
        payload: {
          ap_status: detail.currentlyHolds
            ? AP_STATUS_ACTIVE
            : AP_STATUS_FORMER,
          persona_autofill_id: values.personaAutofillId,
          role_id: type.uuid,
          designation: null,
          appointed_on: toApiDate(detail.startDate),
          resigned_on: toApiDate(detail.endDate),
          is_authorised_signatory: isSignatory,
        },
      });
    } else {
      newRoles.push({
        role_type: PERSONA_TYPE_OFFICER,
        role_id: type.uuid,
        appointed_on: toApiDate(detail.startDate),
        resigned_on: toApiDate(detail.endDate),
        ap_status: detail.currentlyHolds ? AP_STATUS_ACTIVE : AP_STATUS_FORMER,
        is_authorised_signatory: isSignatory,
      });
    }
    signatoryAssigned = signatoryAssigned || values.isAuthorisedSignatory;
  });

  if (values.selectedPills.includes(DIRECTOR_PILL)) {
    const detail: RoleDetail =
      values.roleDetails[DIRECTOR_PILL] || emptyRoleDetail();
    const isSignatory = values.isAuthorisedSignatory && !signatoryAssigned;
    if (detail.joinId) {
      updates.push({
        kind: "AP_UPDATE",
        personaType: PERSONA_TYPE_DIRECTOR,
        joinId: detail.joinId,
        payload: {
          ap_status: detail.currentlyHolds
            ? AP_STATUS_ACTIVE
            : AP_STATUS_FORMER,
          persona_autofill_id: values.personaAutofillId,
          role_id: null,
          designation: null,
          appointed_on: toApiDate(detail.startDate),
          resigned_on: toApiDate(detail.endDate),
          is_authorised_signatory: isSignatory,
        },
      });
    } else {
      newRoles.push({
        role_type: PERSONA_TYPE_DIRECTOR,
        appointed_on: toApiDate(detail.startDate),
        resigned_on: toApiDate(detail.endDate),
        ap_status: detail.currentlyHolds ? AP_STATUS_ACTIVE : AP_STATUS_FORMER,
        is_authorised_signatory: isSignatory,
      });
    }
    signatoryAssigned = signatoryAssigned || values.isAuthorisedSignatory;
  }

  if (values.selectedPills.includes(OTHER_PILL)) {
    (values.otherRoles as OtherRoleEntry[])
      .filter((entry) => entry.roleName)
      .forEach((entry) => {
        const isSignatory = values.isAuthorisedSignatory && !signatoryAssigned;
        if (entry.joinId) {
          updates.push({
            kind: "AP_UPDATE",
            personaType: PERSONA_TYPE_OFFICER,
            joinId: entry.joinId,
            payload: {
              ap_status: entry.currentlyHolds
                ? AP_STATUS_ACTIVE
                : AP_STATUS_FORMER,
              persona_autofill_id: values.personaAutofillId,
              role_id: null,
              designation: entry.roleName,
              appointed_on: toApiDate(entry.startDate),
              resigned_on: toApiDate(entry.endDate),
              is_authorised_signatory: isSignatory,
            },
          });
        } else {
          newRoles.push({
            role_type: PERSONA_TYPE_OFFICER,
            role_id: null,
            designation: entry.roleName,
            appointed_on: toApiDate(entry.startDate),
            resigned_on: toApiDate(entry.endDate),
            ap_status: entry.currentlyHolds
              ? AP_STATUS_ACTIVE
              : AP_STATUS_FORMER,
            is_authorised_signatory: isSignatory,
          });
        }
        signatoryAssigned = signatoryAssigned || values.isAuthorisedSignatory;
      });
  }

  if (values.selectedPills.includes(SHAREHOLDER_PILL)) {
    if (activeShareholder) {
      updates.push({
        kind: "SHAREHOLDER_UPDATE",
        shareholderId: activeShareholder.uuid,
        payload: {
          instance_id: values.personaAutofillId,
          ownership_stake: String(values.shareholder.ownershipPercentage),
          shareholder_type: SHAREHOLDER_TYPE_PERSONA,
          country_id: values.shareholder.countryId,
          issued_common_stock:
            values.shareholder.issuedCommonStock !== ""
              ? Number(values.shareholder.issuedCommonStock)
              : null,
          issued_preferred_stock:
            values.shareholder.issuedPreferredStock !== ""
              ? Number(values.shareholder.issuedPreferredStock)
              : null,
        },
      });
    } else {
      newRoles.push({
        role_type: PERSONA_TYPE_SHAREHOLDER,
        ownership_stake: String(values.shareholder.ownershipPercentage),
        country_id: values.shareholder.countryId,
        issued_common_stock:
          values.shareholder.issuedCommonStock !== ""
            ? Number(values.shareholder.issuedCommonStock)
            : null,
        issued_preferred_stock:
          values.shareholder.issuedPreferredStock !== ""
            ? Number(values.shareholder.issuedPreferredStock)
            : null,
      });
    }
  }

  return { updates, newRoles };
};
